import { ColumnDef } from '@tanstack/react-table';
import { CheckCircle, DollarSign, FileText, Receipt, Search, ShieldX, Truck, User, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { romaneioService } from '../../api';
import type { EntregoSaldoParams } from '../../api';
import {
  DataTable,
  NoOpsSelected,
  PageError,
  PageLoading,
  RefreshButton,
} from '../../components';
import { createSortableHeader } from '../../components/DataTable';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { StatBox } from '../../components/ui/stat-box';
import { Tabs, TabsContent, PageTabsList, PageTabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../auth/AuthContext';
import { BloqueiosTab } from './components/bloqueios';
import { useOps } from '../../hooks/useOps';
import { useAppSelector } from '../../store';
import { formatCpfCnpj, formatCurrencyBrlFromCents, formatDateIso } from '../../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SaldoInfo {
  possui_saldo: boolean;
  total_registros?: number;
  total_valor?: number;
  total_valor_sem_gorjeta?: number;
  registros?: any[];
  periodo_inicio?: string;
  periodo_fim?: string;
  motivo?: string;
  erro?: string;
}

interface CadastroComSaldo {
  id: number;
  nome_completo: string;
  cpf: string;
  cnpj: string;
  telefone: string;
  ops: string;
  ifood_uuid?: string;
  saldo_info: SaldoInfo;
}

// ─── Tipo de emissão ─────────────────────────────────────────────────────────

// doc_cadastro_wts.tipo_emissao_nf → nome completo; NULL vira 'Não definido'
const TIPO_EMISSAO_LABEL: Record<string, string> = {
  dues: 'DUES',
  en: 'Emissor Nacional',
  propria: 'Emissão Própria',
};
const TIPO_EMISSAO_NAO_DEFINIDO = 'Não definido';
const TIPOS_EMISSAO = [...Object.values(TIPO_EMISSAO_LABEL), TIPO_EMISSAO_NAO_DEFINIDO];

// ─── Columns ─────────────────────────────────────────────────────────────────

const listColumns: ColumnDef<any>[] = [
  {
    accessorKey: 'nome',
    header: createSortableHeader('Nome'),
    cell: ({ row }) => (
      <div className="font-medium text-[hsl(210,50%,10%)]">{row.getValue('nome')}</div>
    ),
  },
  { accessorKey: 'documento', header: 'CPF/CNPJ', size: 150 },
  { accessorKey: 'telefone', header: 'Telefone', size: 130 },
  { accessorKey: 'tipoEmissao', header: createSortableHeader('Tipo de Emissão'), size: 160 },
  {
    accessorKey: 'antecipou',
    header: createSortableHeader('Antecipou (30 dias)'),
    size: 150,
    cell: ({ getValue }) =>
      getValue() ? (
        <Badge className="bg-[hsl(142,76%,36%)]/10 text-[hsl(142,76%,36%)] border border-[hsl(142,76%,36%)]/20 hover:bg-[hsl(142,76%,36%)]/20">
          Sim
        </Badge>
      ) : (
        <Badge variant="outline" className="text-[hsl(215,16%,47%)]">
          Não
        </Badge>
      ),
  },
];

const saldoRegistrosColumns: ColumnDef<any>[] = [
  {
    accessorKey: 'data_romaneio',
    header: createSortableHeader('Data'),
    cell: ({ getValue }) => formatDateIso(getValue() as string) || '',
  },
  { accessorKey: 'ops', header: 'Ops', size: 100 },
  {
    accessorKey: 'valor',
    header: 'Valor Total',
    cell: ({ getValue }) => formatCurrencyBrlFromCents(getValue() as number),
  },
  {
    accessorKey: 'valor_sem_gorjeta',
    header: 'Valor s/ Gorjeta',
    cell: ({ getValue }) => formatCurrencyBrlFromCents(getValue() as number),
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function StatusBadge({ ok, labelTrue, labelFalse }: { ok: boolean; labelTrue: string; labelFalse: string }) {
  return (
    <Badge
      variant={ok ? 'default' : 'destructive'}
      className={
        ok
          ? 'bg-[hsl(142,76%,36%)]/10 text-[hsl(142,76%,36%)] border border-[hsl(142,76%,36%)]/20 hover:bg-[hsl(142,76%,36%)]/20'
          : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
      }
    >
      {ok ? labelTrue : labelFalse}
    </Badge>
  );
}
function SummaryMetric({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-[hsl(215,16%,47%)] mb-1 uppercase tracking-wide">
              {label}
            </p>
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
          </div>
          <div className={`${color} opacity-80`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

// variant 'imile': saldo da quinzena em romaneio_imile_financeiro (casado por CPF),
// sem iFood UUID nem gorjeta.
export function RomaneioEntregadoresEntrego({ variant = 'entrego' }: { variant?: 'entrego' | 'imile' } = {}) {
  const isImile = variant === 'imile';
  const saldoColumns = isImile
    ? saldoRegistrosColumns.filter((c) => (c as { accessorKey?: string }).accessorKey !== 'valor_sem_gorjeta')
    : saldoRegistrosColumns;
  // iMile não emite NFS: sem coluna/card de tipo de emissão
  const columns = isImile
    ? listColumns.filter((c) => (c as { accessorKey?: string }).accessorKey !== 'tipoEmissao')
    : listColumns;
  const { currentOps, getCurrentOpsConfig } = useOps();
  const { hasPermission } = useAuth();
  const podeVerBloqueios = hasPermission('romaneioCadastroBlockList', currentOps || undefined);
  const { opsConfigs } = useAppSelector((state) => state.ops);

  // ── Lista state ──────────────────────────────────────────────────────────
  const [listData, setListData] = useState<any[]>([]);
  const [totalAntecipado, setTotalAntecipado] = useState<number>(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string>('');

  // ── Checagem (saldo) state ───────────────────────────────────────────────
  const [documento, setDocumento] = useState<string>('');
  const [searchResults, setSearchResults] = useState<CadastroComSaldo[]>([]);
  const [checagemLoading, setChecagemLoading] = useState(false);
  const [checagemError, setChecagemError] = useState<string>('');
  const [searchPerformed, setSearchPerformed] = useState(false);

  // ── Load list ────────────────────────────────────────────────────────────

  const loadList = useCallback(async () => {
    if (!currentOps) return;
    const opsConfig = getCurrentOpsConfig();
    const dbName = opsConfig?.dbName;
    if (!dbName) return;

    setListLoading(true);
    setListError('');
    try {
      const response = await romaneioService.getCadastroView({ dbName, ops: currentOps });

      let totalAntecipadoValue = 0;
      if (response.data?.totalAntecipado?.length > 0) {
        const raw = response.data.totalAntecipado[0]?.total_valor_compra;
        const cents = typeof raw === 'string' ? parseInt(raw, 10) : raw || 0;
        totalAntecipadoValue = cents / 100;
      }
      setTotalAntecipado(totalAntecipadoValue);

      let dataArray: any[] = [];
      if (response.data) {
        if (response.data.cadastro && Array.isArray(response.data.cadastro)) {
          dataArray = response.data.cadastro;
        } else if (response.data.data?.cadastro && Array.isArray(response.data.data.cadastro)) {
          dataArray = response.data.data.cadastro;
        } else if (Array.isArray(response.data)) {
          dataArray = response.data;
        } else if (response.data.cadastros && Array.isArray(response.data.cadastros)) {
          dataArray = response.data.cadastros;
        }
      }
      setListData(
        dataArray.map((item: any, index: number) => ({
          id: index + 1,
          nome: item.nome_completo || item.nome || 'N/A',
          documento: item.numero_documento || item.cpf || item.cnpj || 'N/A',
          telefone: item.telefone || 'N/A',
          tipoEmissao: TIPO_EMISSAO_LABEL[item.tipo_emissao_nf] ?? TIPO_EMISSAO_NAO_DEFINIDO,
          antecipou: !!item.antecipou_30d,
          ...item,
        }))
      );
    } catch (err: any) {
      setListError(err.message || 'Erro ao carregar dados de cadastro');
    } finally {
      setListLoading(false);
    }
  }, [currentOps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentOps) return;
    const opsConfig = getCurrentOpsConfig();
    if (!opsConfig?.dbName) return;
    const controller = new AbortController();
    let mounted = true;

    (async () => {
      setListLoading(true);
      setListError('');
      try {
        const response = await romaneioService.getCadastroView({
          dbName: opsConfig.dbName as string,
          ops: currentOps,
        });
        if (controller.signal.aborted || !mounted) return;

        let totalAntecipadoValue = 0;
        if (response.data?.totalAntecipado?.length > 0) {
          const raw = response.data.totalAntecipado[0]?.total_valor_compra;
          const cents = typeof raw === 'string' ? parseInt(raw, 10) : raw || 0;
          totalAntecipadoValue = cents / 100;
        }
        setTotalAntecipado(totalAntecipadoValue);

        let dataArray: any[] = [];
        if (response.data) {
          if (response.data.cadastro && Array.isArray(response.data.cadastro)) {
            dataArray = response.data.cadastro;
          } else if (response.data.data?.cadastro && Array.isArray(response.data.data.cadastro)) {
            dataArray = response.data.data.cadastro;
          } else if (Array.isArray(response.data)) {
            dataArray = response.data;
          } else if (response.data.cadastros && Array.isArray(response.data.cadastros)) {
            dataArray = response.data.cadastros;
          }
        }
        setListData(
          dataArray.map((item: any, index: number) => ({
            id: index + 1,
            nome: item.nome_completo || item.nome || 'N/A',
            documento: item.numero_documento || item.cpf || item.cnpj || 'N/A',
            telefone: item.telefone || 'N/A',
            tipoEmissao: TIPO_EMISSAO_LABEL[item.tipo_emissao_nf] ?? TIPO_EMISSAO_NAO_DEFINIDO,
            antecipou: !!item.antecipou_30d,
            ...item,
          }))
        );
      } catch (err: any) {
        if (controller.signal.aborted || !mounted) return;
        setListError(err.message || 'Erro ao carregar dados de cadastro');
      } finally {
        if (!controller.signal.aborted && mounted) setListLoading(false);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [currentOps, opsConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Checagem ─────────────────────────────────────────────────────────────

  const handleSearch = async () => {
    if (!documento.trim()) return;
    const dbName = getCurrentOpsConfig()?.dbName;
    if (!dbName || !currentOps) {
      setChecagemError('Configuração de banco de dados não encontrada. Aguarde...');
      return;
    }
    setChecagemLoading(true);
    setChecagemError('');
    setSearchResults([]);
    try {
      const doc = documento.trim().replace(/\D/g, '');
      const params: EntregoSaldoParams = { dbName, ops: currentOps };
      if (doc.length === 11) params.cpf = doc;
      else if (doc.length === 14) params.cnpj = doc;
      else params.telefone = doc;

      const response = isImile
        ? await romaneioService.getSaldoImile(params)
        : await romaneioService.getSaldoEntrego(params);
      const registros: CadastroComSaldo[] = response.data?.registros || [];
      if (registros.length > 0) {
        setSearchResults(registros);
      } else {
        setChecagemError('Nenhum cadastro encontrado para o documento informado');
      }
      setSearchPerformed(true);
    } catch (err: any) {
      setChecagemError(err.message || 'Erro ao consultar dados do entregador');
      setSearchPerformed(true);
    } finally {
      setChecagemLoading(false);
    }
  };

  const clearSearch = () => {
    setDocumento('');
    setSearchResults([]);
    setChecagemError('');
    setSearchPerformed(false);
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (!currentOps) {
    return <NoOpsSelected message="Selecione uma operação para acessar os entregadores." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[hsl(210,50%,10%)]">
            Entregadores
          </h1>
          <p className="text-[hsl(215,16%,47%)]">Lista e checagem individual de entregadores</p>
        </div>
      </div>

      <Tabs defaultValue="lista" className="w-full">
        <PageTabsList>
          <PageTabsTrigger value="lista">
            <Users className="h-4 w-4" />
            Lista
          </PageTabsTrigger>
          <PageTabsTrigger value="checagem">
            <Search className="h-4 w-4" />
            Checagem Individual
          </PageTabsTrigger>
          {podeVerBloqueios && (
            <PageTabsTrigger value="bloqueios">
              <ShieldX className="h-4 w-4" />
              Bloqueios
            </PageTabsTrigger>
          )}
        </PageTabsList>

        {/* ── Tab: Bloqueios ─────────────────────────────────────────────── */}
        {podeVerBloqueios && (
          <TabsContent value="bloqueios" className="mt-0 space-y-5">
            <BloqueiosTab />
          </TabsContent>
        )}

        {/* ── Tab: Lista ─────────────────────────────────────────────────── */}
        <TabsContent value="lista" className="mt-0 space-y-5">
          <div className="flex justify-end">
            <RefreshButton onClick={loadList} loading={listLoading} />
          </div>
          <PageError error={listError} />
          <PageLoading loading={listLoading} message="Carregando lista de entregadores..." />
          {!listLoading && (
            <>
              <div className={`grid gap-4 md:grid-cols-2 ${isImile ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
                <StatBox
                  title="Total Antecipado"
                  value={totalAntecipado}
                  valueType="currency"
                  icon={<DollarSign className="h-6 w-6 text-[hsl(142,76%,36%)]" />}
                />
                <StatBox
                  title="Total de Entregadores"
                  value={listData.length}
                  valueType="integer"
                  icon={<Users className="h-6 w-6 text-[hsl(210,35%,32%)]" />}
                />
                <StatBox
                  title="Antecipou (últimos 30 dias)"
                  value={listData.filter((r) => r.antecipou).length}
                  valueType="integer"
                  icon={<CheckCircle className="h-6 w-6 text-[hsl(142,76%,36%)]" />}
                />
                {!isImile && (
                  <Card className="min-h-[140px]" data-testid="card-tipo-emissao">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <p className="text-sm font-medium text-muted-foreground">Tipo de Emissão</p>
                        <Receipt className="h-6 w-6 text-[hsl(210,35%,32%)]" />
                      </div>
                      <dl className="space-y-1 text-sm">
                        {TIPOS_EMISSAO.map((tipo) => (
                          <div key={tipo} className="flex justify-between">
                            <dt className="text-[hsl(215,16%,47%)]">{tipo}</dt>
                            <dd className="font-semibold text-foreground">
                              {listData.filter((r) => r.tipoEmissao === tipo).length}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </CardContent>
                  </Card>
                )}
              </div>
              <DataTable
                data={listData}
                columns={columns}
                title="Entregadores Cadastrados"
                loading={false}
                onRefresh={loadList}
                height={600}
                searchPlaceholder="Buscar por nome, documento ou telefone..."
                pageSize={100}
              />
            </>
          )}
        </TabsContent>

        {/* ── Tab: Checagem Individual ────────────────────────────────────── */}
        <TabsContent value="checagem" className="mt-0 space-y-5">
          <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                Consulta por Documento
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label
                    htmlFor="documento"
                    className="text-[hsl(210,50%,10%)] font-medium text-sm mb-1.5 block"
                  >
                    CPF ou CNPJ do Entregador
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(215,16%,47%)]" />
                    <Input
                      id="documento"
                      value={documento}
                      onChange={(e) => setDocumento(formatCpfCnpj(e.target.value))}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      maxLength={18}
                      className="pl-10 h-10 bg-[hsl(210,40%,98%)] border-[hsl(214,32%,91%)] focus:border-[hsl(43,56%,61%)]"
                    />
                  </div>
                  {(() => {
                    const digits = documento.replace(/\D/g, '');
                    if (digits.length === 0 || digits.length === 11 || digits.length === 14) return null;
                    return (
                      <p className="mt-1.5 text-xs text-[hsl(215,16%,47%)]">
                        CPF: 11 dígitos&nbsp;&nbsp;·&nbsp;&nbsp;CNPJ: 14 dígitos
                        {digits.length > 0 && digits.length < 14 && (
                          <span className="ml-1 text-amber-600">
                            ({digits.length} digitado{digits.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </p>
                    );
                  })()}
                </div>
                <Button
                  onClick={handleSearch}
                  disabled={checagemLoading || !documento.trim()}
                  className="h-10 px-5 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white"
                >
                  {checagemLoading ? (
                    <><div className="animate-spin mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full" />Buscando...</>
                  ) : (
                    <><Search className="h-4 w-4 mr-2" />Buscar</>
                  )}
                </Button>
                {(searchResults.length > 0 || checagemError) && (
                  <Button variant="outline" onClick={clearSearch} className="h-10">Limpar</Button>
                )}
              </div>
            </CardContent>
          </Card>

          <PageError error={checagemError} />
          <PageLoading loading={checagemLoading} message="Consultando dados do entregador..." />

          {searchResults.length > 0 && !checagemLoading && (
            <div className="space-y-6">
              {searchResults.map((cadastro, index) => (
                <Card key={cadastro.id ?? index} className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <div>
                        <CardTitle className="text-sm font-semibold text-[hsl(210,50%,10%)]">
                          {cadastro.nome_completo || 'Sem nome'}
                        </CardTitle>
                        <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                          {cadastro.cpf || cadastro.cnpj} · {cadastro.telefone} · ops: {cadastro.ops}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-auto">
                        {!isImile && (
                          <StatusBadge ok={!!cadastro.ifood_uuid} labelTrue="Com iFood UUID" labelFalse="Sem iFood UUID" />
                        )}
                        <StatusBadge ok={!!cadastro.saldo_info?.possui_saldo} labelTrue="Com Saldo" labelFalse="Sem Saldo" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-4">
                    {cadastro.saldo_info?.possui_saldo && (
                      <>
                        <div className={`grid grid-cols-1 ${isImile ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-3`}>
                          <SummaryMetric
                            label="Registros"
                            value={cadastro.saldo_info.total_registros ?? 0}
                            icon={<FileText className="h-6 w-6" />}
                            color="text-[hsl(210,35%,32%)]"
                          />
                          <SummaryMetric
                            label="Total Valor"
                            value={formatCurrencyBrlFromCents(cadastro.saldo_info.total_valor ?? 0)}
                            icon={<DollarSign className="h-6 w-6" />}
                            color="text-[hsl(142,76%,36%)]"
                          />
                          {!isImile && (
                            <SummaryMetric
                              label="Valor s/ Gorjeta"
                              value={formatCurrencyBrlFromCents(cadastro.saldo_info.total_valor_sem_gorjeta ?? 0)}
                              icon={<Truck className="h-6 w-6" />}
                              color="text-[hsl(230,50%,50%)]"
                            />
                          )}
                        </div>
                        <DataTable
                          columns={saldoColumns}
                          data={cadastro.saldo_info.registros ?? []}
                          searchPlaceholder="Buscar por data ou ops..."
                        />
                      </>
                    )}
                    {!cadastro.saldo_info?.possui_saldo && (
                      <p className="text-sm text-[hsl(215,16%,47%)] py-2">
                        {cadastro.saldo_info?.motivo || cadastro.saldo_info?.erro || 'Nenhum registro de saldo encontrado para este período.'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {searchPerformed && searchResults.length === 0 && !checagemLoading && !checagemError && (
            <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
              <CardContent className="text-center py-12">
                <User className="h-10 w-10 text-[hsl(215,16%,47%)] mx-auto mb-3" />
                <h3 className="text-base font-medium text-[hsl(210,50%,10%)] mb-1">
                  Nenhum resultado encontrado
                </h3>
                <p className="text-sm text-[hsl(215,16%,47%)]">
                  Verifique se o CPF/CNPJ foi digitado corretamente.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
